import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuditAuthMethod, AuditEvent, Paginated } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDateTime,
} from '../../components/ui.js';

const PAGE_SIZE = 50;

function ValueBlock({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  let pretty = value;
  try {
    pretty = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    /* keep raw */
  }
  return (
    <div>
      <strong style={{ fontSize: 'var(--text-xs)' }}>{label}</strong>
      <pre
        style={{
          background: 'var(--color-bg)',
          padding: 8,
          borderRadius: 6,
          fontSize: 11,
          overflowX: 'auto',
          margin: '4px 0',
        }}
      >
        {pretty}
      </pre>
    </div>
  );
}

export function AuditPage() {
  const { org } = useOrg();
  const isAdmin = org.role === 'admin';
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [authMethod, setAuthMethod] = useState<AuditAuthMethod | ''>('');
  const [offset, setOffset] = useState(0);

  const audit = useQuery({
    queryKey: ['audit', org.orgId, action, entityType, from, to, authMethod, offset],
    queryFn: () =>
      apiFetch<Paginated<AuditEvent>>(`/orgs/${org.orgId}/audit`, {
        query: {
          limit: PAGE_SIZE,
          offset,
          action: action.trim() || undefined,
          entityType: entityType.trim() || undefined,
          authMethod: authMethod || undefined,
          from: from ? `${from}T00:00:00.000Z` : undefined,
          to: to ? `${to}T23:59:59.999Z` : undefined,
        },
      }),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <EmptyState title="Admins only" message="The audit log is available to organisation admins." />
    );
  }
  if (audit.isLoading && offset === 0) return <LoadingPane label="Loading audit log" />;
  const items = audit.data?.items ?? [];
  const total = audit.data?.total ?? 0;

  const filterField = (
    label: string,
    value: string,
    setter: (v: string) => void,
    type = 'text',
    placeholder = '',
  ) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--text-xs)', fontWeight: 600 }}>
      {label}
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          setter(e.target.value);
          setOffset(0);
        }}
        style={{ width: type === 'date' ? 160 : 200 }}
      />
    </label>
  );

  return (
    <div>
      <PageHeader
        title="Audit log"
        subtitle="Every meaningful action in this organisation, immutably recorded (PLAN §21)"
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {filterField('Action', action, setAction, 'text', 'e.g. response.status_changed')}
        {filterField('Entity type', entityType, setEntityType, 'text', 'e.g. response')}
        {filterField('From', from, setFrom, 'date')}
        {filterField('To', to, setTo, 'date')}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--text-xs)', fontWeight: 600 }}>
          Method
          <Select
            value={authMethod}
            onChange={(e) => {
              setAuthMethod(e.target.value as AuditAuthMethod | '');
              setOffset(0);
            }}
            style={{ width: 160 }}
          >
            <option value="">Any method</option>
            <option value="session">Browser session</option>
            <option value="api_token">API token</option>
          </Select>
        </label>
      </div>

      <Table
        head={
          <>
            <th>Time</th>
            <th>User</th>
            <th>Action</th>
            <th>Entity</th>
            <th>Details</th>
          </>
        }
      >
        {items.map((e) => (
          <tr key={e.id}>
            <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.createdAt)}</td>
            <td>
              {e.userName ?? (e.userId ? e.userId.slice(0, 12) : 'system')}
              {e.authMethod === 'api_token' && (
                <div style={{ marginTop: 2 }}>
                  <Badge tone="neutral">API token{e.apiTokenName ? ` · ${e.apiTokenName}` : ''}</Badge>
                </div>
              )}
            </td>
            <td>
              <Badge tone="neutral">{e.action}</Badge>
            </td>
            <td>
              {e.entityType}
              {e.entityId && (
                <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                  {e.entityId.slice(0, 18)}…
                </div>
              )}
            </td>
            <td>
              {(e.previousValue || e.newValue) && (
                <details>
                  <summary style={{ cursor: 'pointer', color: 'var(--color-primary)' }}>values</summary>
                  <ValueBlock label="Previous" value={e.previousValue} />
                  <ValueBlock label="New" value={e.newValue} />
                </details>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {items.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', padding: 16 }}>No events match these filters.</p>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
